import { SDK_GET_RETRY_COUNT, SDK_TIMEOUT_LADDER_MS } from '@repo/config';
import { getRetryDelayMs } from './get-retry-delay-ms';
import { isRetryableError } from './is-retryable-error';
import { isRetryableStatus } from './is-retryable-status';
import type { IMakeRawRequestParams } from './make-raw-request';
import { makeRawRequest } from './make-raw-request';

const waitMs = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

function resolveAttemptTimeout(params: IMakeRawRequestParams, attemptIndex: number): number {
  if (params.options?.timeout) {
    return params.options.timeout;
  }
  return (
    SDK_TIMEOUT_LADDER_MS[Math.min(attemptIndex, SDK_TIMEOUT_LADDER_MS.length - 1)] ??
    SDK_TIMEOUT_LADDER_MS[0] ??
    30_000
  );
}

async function runRetryableAttempt(
  params: IMakeRawRequestParams,
  attemptIndex: number,
  isLastAttempt: boolean,
): Promise<Response | null> {
  const timeout = resolveAttemptTimeout(params, attemptIndex);
  const attemptParams = { ...params, options: { ...params.options, timeout } };
  try {
    const response = await makeRawRequest(attemptParams);
    if (!isLastAttempt && isRetryableStatus(response.status)) {
      return null;
    }
    return response;
  } catch (error) {
    if (!isLastAttempt && isRetryableError(error)) {
      return null;
    }
    throw error;
  }
}

export async function makeRawRequestWithRetries(params: IMakeRawRequestParams): Promise<Response> {
  const totalAttempts = params.method === 'GET' ? SDK_GET_RETRY_COUNT + 1 : 1;

  for (let attemptIndex = 0; attemptIndex < totalAttempts; attemptIndex++) {
    const isLastAttempt = attemptIndex === totalAttempts - 1;
    const response = await runRetryableAttempt(params, attemptIndex, isLastAttempt);
    if (response) {
      return response;
    }
    await waitMs(getRetryDelayMs(attemptIndex));
  }

  return makeRawRequest(params);
}
